import React, { useEffect, useState } from "react";
import { RotateCcw, ChevronDown, ChevronRight } from "lucide-react";

// Filters of the Nivel 1 / Nivel 2 tables (4-nov-2026), in the style of Visual's: each one
// narrows the rows by a column's content. An empty number box = no filter on that column.
// Remembered per page in the browser.
export const LEVEL_FILTER_DEFAULTS = { q: "", sector: "all", mode: "all", rc: "", rv: "", score: "", tam: "", kpi: "" };

const num = (v) => (v === "" || v == null ? null : Number(v));

export function useLevelFilters(storageKey) {
    const [filters, setFilters] = useState(() => {
        try { return { ...LEVEL_FILTER_DEFAULTS, ...(JSON.parse(window.localStorage.getItem(storageKey) || "null") || {}) }; }
        catch { return { ...LEVEL_FILTER_DEFAULTS }; }
    });
    useEffect(() => {
        try { window.localStorage.setItem(storageKey, JSON.stringify(filters)); } catch { /* ignore */ }
    }, [storageKey, filters]);
    return [filters, setFilters];
}

export const levelFiltersActive = (f) => Object.keys(LEVEL_FILTER_DEFAULTS).some((k) => f[k] !== LEVEL_FILTER_DEFAULTS[k]);

// `row`: { ticker, name, sector, mode, rc, rv, score, tam, kpi } (plain values of the row).
export function passesLevelFilters(row, f) {
    const q = (f.q || "").trim().toLowerCase();
    if (q && !`${row.ticker || ""} ${row.name || ""}`.toLowerCase().includes(q)) return false;
    if (f.sector !== "all" && (row.sector || "Sin clasificar") !== f.sector) return false;
    if (f.mode !== "all" && (row.mode === "manual" ? "manual" : "auto") !== f.mode) return false;
    for (const k of ["rc", "rv", "score", "tam", "kpi"]) {
        const min = num(f[k]);
        if (min == null || Number.isNaN(min)) continue;
        if (typeof row[k] !== "number" || row[k] < min) return false;
    }
    return true;
}

const Field = ({ label, value, step, onChange, testid, suffix }) => (
    <label className="block min-w-0">
        <div className="text-[11px] text-[#4A4A4A] font-mono mb-1 truncate">{label}</div>
        <div className="flex items-center">
            <input
                type="number"
                inputMode="decimal"
                value={value}
                step={step}
                placeholder="—"
                onChange={(e) => onChange(e.target.value)}
                className="border border-black px-2 py-1 text-sm font-mono w-full min-w-0 bg-white"
                data-testid={testid}
            />
            {suffix && <span className="ml-1 text-xs text-[#4A4A4A] font-mono">{suffix}</span>}
        </div>
    </label>
);

export function LevelFilters({ filters, setFilters, sectorOptions, shown, total, testid = "level-filters" }) {
    const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
    const active = levelFiltersActive(filters);
    // On a phone the fields fold away (they take ~4 rows): closed by default, the header
    // still says how many companies the active filters leave. From `sm` up, always open.
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-black/20 p-4 mb-4 bg-[#FAF6EE]" data-testid={testid}>
            <div className={`flex items-center justify-between gap-3 flex-wrap ${open ? "mb-3" : "mb-0"} sm:mb-3`}>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                    className="overline text-[#4A4A4A] inline-flex items-center gap-1 text-left sm:pointer-events-none"
                    data-testid={`${testid}-toggle`}
                >
                    <span className="sm:hidden">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                    Filtros{active ? <> · <span className="text-black">{shown} de {total} empresas</span></> : ""}
                    <span className="sm:hidden ml-1 normal-case tracking-normal text-[11px] underline">{open ? "Ocultar" : "Mostrar"}</span>
                </button>
                <button
                    onClick={() => setFilters({ ...LEVEL_FILTER_DEFAULTS })}
                    disabled={!active}
                    className="text-xs font-mono text-[#4A4A4A] hover:text-black flex items-center gap-1 disabled:opacity-40"
                    data-testid={`${testid}-reset`}
                >
                    <RotateCcw size={12} /> Reset
                </button>
            </div>
            <div className={`${open ? "grid" : "hidden"} sm:grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3`} data-testid={`${testid}-fields`}>
                <label className="block min-w-0 col-span-2">
                    <div className="text-[11px] text-[#4A4A4A] font-mono mb-1">Ticker o nombre</div>
                    <input
                        type="search"
                        value={filters.q}
                        onChange={(e) => set("q")(e.target.value)}
                        placeholder="Buscar…"
                        className="border border-black px-2 py-1 text-sm font-mono w-full min-w-0 bg-white"
                        data-testid={`${testid}-q`}
                    />
                </label>
                <label className="block min-w-0">
                    <div className="text-[11px] text-[#4A4A4A] font-mono mb-1">Sector</div>
                    <select value={filters.sector} onChange={(e) => set("sector")(e.target.value)}
                            className="border border-black px-2 py-1 text-sm font-mono w-full min-w-0 bg-white" data-testid={`${testid}-sector`}>
                        <option value="all">Todos</option>
                        {sectorOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <label className="block min-w-0">
                    <div className="text-[11px] text-[#4A4A4A] font-mono mb-1">Modo</div>
                    <select value={filters.mode} onChange={(e) => set("mode")(e.target.value)}
                            className="border border-black px-2 py-1 text-sm font-mono w-full min-w-0 bg-white" data-testid={`${testid}-mode`}>
                        <option value="all">Todos</option>
                        <option value="auto">AUTO</option>
                        <option value="manual">MAN</option>
                    </select>
                </label>
                <Field label="Ratio Compra ≥" value={filters.rc} step={5} onChange={set("rc")} suffix="%" testid={`${testid}-rc`} />
                <Field label="Ratio Venta ≥" value={filters.rv} step={5} onChange={set("rv")} suffix="%" testid={`${testid}-rv`} />
                <Field label="Score ≥" value={filters.score} step={1} onChange={set("score")} testid={`${testid}-score`} />
                <Field label="TAM Score ≥" value={filters.tam} step={0.5} onChange={set("tam")} testid={`${testid}-tam`} />
                <Field label="Coef KPI ≥" value={filters.kpi} step={0.1} onChange={set("kpi")} testid={`${testid}-kpi`} />
            </div>
        </div>
    );
}
